import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type ProviderAccountType = 'PERSON' | 'BUSINESS' | '';
export type ProviderPlan = 'BASIC' | 'PREMIUM' | '';

export type ProviderApplicationDraft = {
  accountType: ProviderAccountType;
  plan: ProviderPlan;
  fullName: string;
  businessName: string;
  representativeName: string;
  email: string;
  phone: string;
  description: string;
  storePickupEnabled: boolean;
  iban: string;
  termsAccepted: boolean;
};

export type ProviderApplicationState = {
  step: number;
  draft: ProviderApplicationDraft;
};

export const PROVIDER_APPLICATION_CACHE_KEY = 'emdk.providerApplicationDraft';

export const emptyProviderApplicationDraft: ProviderApplicationDraft = {
  accountType: '',
  plan: '',
  fullName: '',
  businessName: '',
  representativeName: '',
  email: '',
  phone: '',
  description: '',
  storePickupEnabled: false,
  iban: '',
  termsAccepted: false,
};

const getInitialState = (): ProviderApplicationState => {
  const fallback = { step: 0, draft: emptyProviderApplicationDraft };
  if (typeof window === 'undefined') return fallback;

  try {
    const cached = window.sessionStorage.getItem(PROVIDER_APPLICATION_CACHE_KEY);
    if (!cached) return fallback;
    const parsed = JSON.parse(cached) as Partial<ProviderApplicationState>;
    return {
      step: Math.min(Math.max(Number(parsed.step) || 0, 0), 4),
      draft: { ...emptyProviderApplicationDraft, ...(parsed.draft || {}) },
    };
  } catch {
    window.sessionStorage.removeItem(PROVIDER_APPLICATION_CACHE_KEY);
    return fallback;
  }
};

const providerApplicationSlice = createSlice({
  name: 'providerApplication',
  initialState: getInitialState,
  reducers: {
    updateProviderDraft: (
      state,
      action: PayloadAction<{ field: keyof ProviderApplicationDraft; value: string | boolean }>,
    ) => {
      (state.draft as Record<string, string | boolean>)[action.payload.field] = action.payload.value;
    },
    setProviderApplicationStep: (state, action: PayloadAction<number>) => {
      state.step = Math.min(Math.max(action.payload, 0), 4);
    },
    clearProviderApplicationDraft: () => ({
      step: 0,
      draft: { ...emptyProviderApplicationDraft },
    }),
  },
});

export const {
  updateProviderDraft,
  setProviderApplicationStep,
  clearProviderApplicationDraft,
} = providerApplicationSlice.actions;

export default providerApplicationSlice.reducer;
